/**
 * Blog Post Search Web Component
 *
 * Light DOM combobox that fetches matching blog posts from the
 * `felix/v1/posts/search` REST endpoint and renders them in a popover
 * listbox below the input. Keyboard-navigable per the WAI-ARIA combobox
 * authoring practices.
 *
 * The panel lives outside the host element so it can be positioned against
 * a parent row container (full-width takeover). The component finds the
 * panel and status region by id via the `data-listbox-id` and
 * `data-status-id` attributes.
 *
 * @package Felix
 * @since 1.0.0
 */

import { debounce } from '../shared/utils.js'

/** Shared with assets/js/components/site-search.js and assets/js/inline/segment-middleware.js. */
const SEARCH_SOURCE_KEY = 'felix_search_source'

class BlogPostSearch extends HTMLElement {
	constructor() {
		super()
		this.cache = new Map()
		this.items = []
		this.activeIndex = -1
		this.abortController = null
		this.lastQuery = ''
		this.lastTracked = ''
		this.isOpen = false
		this.optionIdPrefix = `blog-post-search-option-${Math.random().toString(36).slice(2, 8)}-`
	}

	connectedCallback() {
		this.restUrl = this.dataset.restUrl
		this.minChars = parseInt(this.dataset.minChars, 10) || 2
		this.debounceMs = parseInt(this.dataset.debounce, 10) || 250
		this.perPage = parseInt(this.dataset.perPage, 10) || 6
		this.context = this.dataset.context || 'blog'
		this.sendTerms = this.dataset.sendTerms === '1'

		this.input = this.querySelector('input')
		this.panel = document.getElementById(this.dataset.listboxId)
		this.statusEl = document.getElementById(this.dataset.statusId)
		this.scrim = this.dataset.scrimId ? document.getElementById(this.dataset.scrimId) : null

		if (!this.input || !this.panel) return

		this.input.setAttribute('role', 'combobox')
		this.input.setAttribute('aria-controls', this.dataset.listboxId)
		this.input.setAttribute('aria-autocomplete', 'list')
		this.input.setAttribute('aria-expanded', 'false')
		this.input.setAttribute('spellcheck', 'false')

		this.fetchDebounced = debounce((q) => this.fetchResults(q), this.debounceMs)

		this.input.addEventListener('input', this.handleInput)
		this.input.addEventListener('keydown', this.handleKeydown)
		this.input.addEventListener('focus', this.handleFocus)
		document.addEventListener('focusin', this.handleDocumentFocusin)
		document.addEventListener('keydown', this.handleDocumentKeydown)
		document.addEventListener('pointerdown', this.handleDocumentPointerdown)
		this.panel.addEventListener('mousedown', this.handlePanelMousedown)
		this.panel.addEventListener('click', this.handleResultClick)
		window.addEventListener('resize', this.handleViewportChange)
		window.addEventListener('scroll', this.handleViewportChange, { passive: true })
	}

	disconnectedCallback() {
		this.input?.removeEventListener('input', this.handleInput)
		this.input?.removeEventListener('keydown', this.handleKeydown)
		this.input?.removeEventListener('focus', this.handleFocus)
		document.removeEventListener('focusin', this.handleDocumentFocusin)
		document.removeEventListener('keydown', this.handleDocumentKeydown)
		document.removeEventListener('pointerdown', this.handleDocumentPointerdown)
		this.panel?.removeEventListener('mousedown', this.handlePanelMousedown)
		this.panel?.removeEventListener('click', this.handleResultClick)
		window.removeEventListener('resize', this.handleViewportChange)
		window.removeEventListener('scroll', this.handleViewportChange)
		this.abortController?.abort()
	}

	handleViewportChange = () => {
		if (!this.isOpen) return
		this.syncPanelTop()
	}

	syncPanelTop() {
		if (!this.panel) return
		const rect = this.getBoundingClientRect()
		const header = document.querySelector('header.wp-block-template-part')
		const headerBottom = header ? header.getBoundingClientRect().bottom : 0
		const spacingAbove = Math.max(0, rect.top - headerBottom)
		const panelTop = rect.bottom + spacingAbove
		this.panel.style.setProperty('--blog-post-search-panel-top', `${Math.round(panelTop)}px`)
	}

	handleInput = () => {
		const query = this.input.value.trim()
		if (query.length < this.minChars) {
			this.abortController?.abort()
			this.items = []
			this.activeIndex = -1
			this.closePanel()
			return
		}
		this.fetchDebounced(query)
	}

	handleFocus = () => {
		if (this.items.length > 0) {
			this.openPanel()
		}
	}

	// Close when focus leaves the input, panel, and scrim.
	handleDocumentFocusin = (event) => {
		if (!this.isOpen) return
		const target = event.target
		if (this.contains(target)) return
		if (this.panel?.contains(target)) return
		this.closePanel()
	}

	handleDocumentKeydown = (event) => {
		if (event.key !== 'Escape' || !this.isOpen) return
		event.preventDefault()
		this.input.value = ''
		this.items = []
		this.activeIndex = -1
		this.closePanel()
		this.input.focus()
	}

	// Close on outside click (anywhere not in the input host or panel).
	handleDocumentPointerdown = (event) => {
		if (!this.isOpen) return
		const target = event.target
		if (this.contains(target)) return
		if (this.panel?.contains(target)) return
		this.closePanel()
	}

	// Prevent the input from losing focus when the user mousedowns on a result;
	// click navigation will still fire on the anchor.
	handlePanelMousedown = (event) => {
		if (event.target.closest('a')) return
		event.preventDefault()
	}

	// Same attribution handoff as site-search.js handleResultClick: the next
	// pageview's Segment call picks this up in segment-middleware.js.
	handleResultClick = (event) => {
		const anchor = event.target.closest('a')
		if (!anchor?.dataset.searchResult) return

		const payload = {
			ts: Date.now(),
			resultType: anchor.dataset.trackResultType || '',
			position: anchor.dataset.trackResultPosition || '',
		}

		if (this.sendTerms && anchor.dataset.trackSearchTerm) {
			payload.term = anchor.dataset.trackSearchTerm
		}

		try {
			sessionStorage.setItem(SEARCH_SOURCE_KEY, JSON.stringify(payload))
		} catch (e) {
			// Storage unavailable; attribution is best-effort.
		}
	}

	handleKeydown = (event) => {
		switch (event.key) {
			case 'ArrowDown':
				event.preventDefault()
				if (this.items.length === 0) return
				this.setActiveIndex((this.activeIndex + 1) % this.items.length)
				break
			case 'ArrowUp':
				event.preventDefault()
				if (this.items.length === 0) return
				this.setActiveIndex((this.activeIndex - 1 + this.items.length) % this.items.length)
				break
			case 'Home':
				if (this.items.length === 0) return
				event.preventDefault()
				this.setActiveIndex(0)
				break
			case 'End':
				if (this.items.length === 0) return
				event.preventDefault()
				this.setActiveIndex(this.items.length - 1)
				break
			case 'Enter': {
				if (this.activeIndex < 0) return
				event.preventDefault()
				const option = this.panel.querySelector(`[data-index="${this.activeIndex}"]`)
				const anchor = option?.querySelector('a[href]')
				if (anchor) anchor.click()
				break
			}
		}
	}

	async fetchResults(query) {
		if (this.cache.has(query)) {
			this.lastQuery = query
			this.renderResults(this.cache.get(query), query)
			return
		}

		this.abortController?.abort()
		this.abortController = new AbortController()

		const url = new URL(this.restUrl)
		url.searchParams.set('s', query)
		url.searchParams.set('per_page', String(this.perPage))

		this.renderLoading(query)

		try {
			const res = await fetch(url.toString(), {
				signal: this.abortController.signal,
				headers: { Accept: 'application/json' },
			})
			if (!res.ok) throw new Error(`HTTP ${res.status}`)
			const data = await res.json()
			this.cache.set(query, data)
			this.lastQuery = query
			this.renderResults(data, query)
		} catch (err) {
			if (err?.name === 'AbortError') return
			this.renderError()
		}
	}

	renderLoading(query) {
		this.panel.innerHTML = `<p class="blog-post-search__message text-body-md text-slate-40 p-24">${this.escape(`Searching for “${query}”…`)}</p>`
		this.openPanel()
	}

	renderError() {
		this.items = []
		this.activeIndex = -1
		this.panel.innerHTML = '<p class="blog-post-search__message text-body-md text-slate-40 p-24">Search is unavailable. Please try again.</p>'
		this.openPanel()
	}

	renderResults(data, query) {
		const items = Array.isArray(data?.items) ? data.items : []
		this.items = items
		this.activeIndex = -1

		this.track(data, items, query)

		if (items.length === 0) {
			this.panel.innerHTML = `<p class="blog-post-search__message text-body-md text-slate-40 p-24">${this.escape(`No posts match “${query}”.`)}</p>`
			this.announceStatus('No results')
			this.openPanel()
			return
		}

		const total = Number.isFinite(data?.total) ? data.total : items.length

		const wrap = document.createElement('div')
		wrap.className = 'blog-post-search__results p-24'

		const grid = document.createElement('ul')
		grid.className = 'blog-post-search__list'
		grid.setAttribute('role', 'presentation')

		items.forEach((item, index) => {
			const li = document.createElement('li')
			li.className = 'blog-post-search__option cursor-pointer'
			li.id = `${this.optionIdPrefix}${index}`
			li.setAttribute('role', 'option')
			li.setAttribute('aria-selected', 'false')
			li.dataset.index = String(index)
			li.innerHTML = item.html
			this.tagResultAnchors(li, item, index, query)
			li.addEventListener('mouseenter', () => this.setActiveIndex(index))
			li.addEventListener('click', (event) => {
				if (event.target.closest('a')) return
				const anchor = li.querySelector('a[href]')
				if (anchor) anchor.click()
			})
			grid.appendChild(li)
		})

		wrap.appendChild(grid)

		if (total > items.length) {
			const footer = document.createElement('p')
			footer.className = 'blog-post-search__footer text-body-sm text-slate-40 mt-16'
			footer.textContent = `Showing top ${items.length} of ${total} results`
			wrap.appendChild(footer)
		}

		this.panel.innerHTML = ''
		this.panel.appendChild(wrap)

		const summary =
			total > items.length
				? `Showing top ${items.length} of ${total} results`
				: `${items.length} ${items.length === 1 ? 'result' : 'results'}`
		this.announceStatus(summary)
		this.openPanel()
	}

	// The card HTML is server-rendered, so the search_result_click attributes the
	// header search sets in its renderer are stamped on after insertion instead.
	tagResultAnchors(li, item, index, query) {
		li.querySelectorAll('a[href]').forEach((anchor) => {
			if (item.url && anchor.href !== item.url) return
			anchor.dataset.trackEvent = 'search_result_click'
			anchor.dataset.trackResultType = 'post'
			anchor.dataset.trackResultPosition = String(index + 1)
			anchor.dataset.trackCategory = 'post'
			anchor.dataset.searchResult = '1'
			if (this.sendTerms) {
				anchor.dataset.trackSearchTerm = query
			}
		})
	}

	// One search_performed per settled search, mirroring site-search.js track().
	track(data, items, query) {
		if (this.lastTracked === query) return
		this.lastTracked = query

		const total = Number.isFinite(data?.total) ? data.total : items.length

		window.felixTrack?.('search_performed', {
			...(this.sendTerms ? { searchTerm: query } : {}),
			resultCount: total,
			resultCategories: total > 0 ? 'post' : '',
			hadResults: total > 0,
			vetoed: false,
			searchContext: this.context,
		})
	}

	setActiveIndex(index) {
		const options = this.panel.querySelectorAll('[role="option"]')
		options.forEach((opt) => opt.setAttribute('aria-selected', 'false'))

		this.activeIndex = index
		const active = options[index]
		if (!active) {
			this.input.removeAttribute('aria-activedescendant')
			return
		}
		active.setAttribute('aria-selected', 'true')
		this.input.setAttribute('aria-activedescendant', active.id)

		if (typeof active.scrollIntoView === 'function') {
			active.scrollIntoView({ block: 'nearest' })
		}
	}

	openPanel() {
		this.isOpen = true
		this.syncPanelTop()
		this.panel.classList.add('is-open')
		this.panel.setAttribute('aria-hidden', 'false')
		this.input.setAttribute('aria-expanded', 'true')
		if (this.scrim) {
			this.scrim.classList.add('is-open')
		}
		document.body.classList.add('blog-post-search-open')
	}

	closePanel() {
		this.isOpen = false
		this.panel.classList.remove('is-open')
		this.panel.setAttribute('aria-hidden', 'true')
		this.input.setAttribute('aria-expanded', 'false')
		this.input.removeAttribute('aria-activedescendant')
		if (this.scrim) {
			this.scrim.classList.remove('is-open')
		}
		document.body.classList.remove('blog-post-search-open')
	}

	announceStatus(message) {
		if (!this.statusEl) return
		this.statusEl.textContent = message
	}

	escape(str) {
		const div = document.createElement('div')
		div.textContent = str
		return div.innerHTML
	}
}

if (!customElements.get('felix-blog-post-search')) {
	customElements.define('felix-blog-post-search', BlogPostSearch)
}
